import React, { useMemo, useState } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { cn, formatDate2 } from '@lib/utils.ts';
import { LineDot } from 'recharts/types/cartesian/Line';
import isNil from 'lodash/isNil';
import { StackedData } from '@src/types';

type StackPayload = {
  value: number;
  payload?: StackedData;
  color?: string;
};

interface TooltipProps {
  active?: boolean;
  payload?: StackPayload[];
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
  const { t } = useTranslation();

  if (active && payload && payload.length) {
    return (
      <div
        className={
          'bg-card-stat p-2.5 rounded-1 text-xs border-1 border-[#3D3D3D] rounded-[8px] flex flex-col items-center min-w-[135px] gap-y-1'
        }
        style={{
          boxShadow: '0px 7px 13px 0px #DC342E14',
        }}
      >
        <p>{`${t('dashboard.winningTrades')}: ${payload[0]?.payload?.wining}`}</p>
        <p>{`${t('dashboard.losingTrades')}: ${payload[0]?.payload?.losing}`}</p>
        <p>{`${t('dashboard.contracts')}: ${payload[0]?.payload?.contracts}`}</p>
        {!isNil(payload[0]?.payload?.micro) && (
          <p>{`Micro: ${payload[0]?.payload?.micro}`}</p>
        )}
        {!isNil(payload[0]?.payload?.mini) && (
          <p>{`Mini: ${payload[0]?.payload?.mini}`}</p>
        )}
        <p className={'text-[#999999]'}>
          {formatDate2(payload[0]?.payload?.date)}
        </p>
      </div>
    );
  }

  return null;
};

interface CustomLegendProps {
  className?: string;
  payload?: StackPayload[];
  onClick: (key: any) => void;
}

const CustomLegend: React.FC<CustomLegendProps> = ({
  className,
  payload,
  onClick,
}) => {
  const { t } = useTranslation();

  if (!payload) return null;

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <p className={'text-xs font-medium'}>
        {t('dashboard.stackedChartTitle')}
      </p>
      <div className={'flex gap-x-3 p-2.5'}>
        {payload.map((entry, index) => (
          <div
            key={index}
            className={'flex items-center gap-x-1.5'}
            onClick={() => onClick(entry)}
          >
            <div
              className={`w-9 h-3`}
              style={{ backgroundColor: entry.color }}
            />
            <p className={'text-xs font-medium'}>{entry.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface StackedChartProps {
  data: StackedData[];
  height?: number;
  barSize?: number;
  legendClassName?: string;
  className?: string;
  dot?: LineDot;
  angle?: number;
}

export const DailyTradeChart: React.FC<StackedChartProps> = ({
  data,
  height = 500,
  barSize = 36,
  className,
  legendClassName,
  dot,
  angle,
}) => {
  const { t } = useTranslation();

  const isMicro = useMemo(() => data.some((item) => item.micro), [data]);

  const isMini = useMemo(() => data.some((item) => item.mini), [data]);

  const [visible, setVisible] = useState({
    wining: true,
    losing: true,
    contracts: true,
    micro: true,
    mini: true,
  });

  const toggle = (dataKey: any) => {
    setVisible((prev: any) => ({
      ...prev,
      [dataKey.dataKey]: !prev[dataKey.dataKey],
    }));
  };

  const filteredData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      wining: visible.wining ? item.wining : 0,
      losing: visible.losing ? item.losing : 0,
      contracts: visible.contracts ? item.contracts : undefined,
      micro: visible.micro ? item.micro : undefined,
      mini: visible.mini ? item.mini : undefined,
    }));
  }, [data, visible]);

  return (
    <ResponsiveContainer
      className={className}
      height={height}
      style={{ margin: 0, padding: 0 }}
      width="100%"
    >
      <ComposedChart data={filteredData}>
        <CartesianGrid stroke="#CECECE" strokeDasharray="0" vertical={false} />
        <XAxis
          angle={angle}
          dataKey="date"
          dy={10}
          fontSize={8}
          padding={{ left: 0, right: 0 }}
          stroke="#CECECE"
          tickFormatter={(value) => formatDate2(value)}
          tickLine={false}
        />
        <YAxis fontSize={8} stroke="#CECECE" tickLine={false} yAxisId="left" />
        <YAxis
          domain={[-30, 30]}
          fontSize={8}
          orientation="right"
          stroke="#CECECE"
          tickLine={false}
          yAxisId="right"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          align="center"
          content={
            <CustomLegend className={legendClassName} onClick={toggle} />
          }
          layout="horizontal"
          verticalAlign="top"
          onMouseEnter={(e) => toggle(e.dataKey)}
        />
        <Bar
          barSize={barSize}
          dataKey="wining"
          fill="#14CA74"
          name={t('dashboard.winningTrades')}
          stackId="a"
          yAxisId="left"
        />
        <Bar
          barSize={barSize}
          dataKey="losing"
          fill="#C72E2E"
          name={t('dashboard.losingTrades')}
          stackId="a"
          yAxisId="left"
        />

        <Line
          dataKey="contracts"
          dot={dot ?? { r: 3, fill: '#8A8A8A' }}
          name={t('dashboard.contracts')}
          stroke="#8A8A8A"
          strokeWidth={2}
          type="monotone"
          yAxisId="right"
        />
        {isMicro && (
          <Line
            dataKey="micro"
            dot={dot ?? { r: 3, fill: '#C1C100' }}
            name={'Micro'}
            stroke="#C1C100"
            strokeWidth={2}
            type="monotone"
            yAxisId="right"
          />
        )}
        {isMini && (
          <Line
            dataKey="mini"
            dot={dot ?? { r: 3, fill: '#000000' }}
            name={'Mini'}
            stroke="#000000"
            strokeWidth={2}
            type="monotone"
            yAxisId="right"
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
